export type CheckoutStatus =
  | 'pending'
  | 'awaiting_payment'
  | 'detected'
  | 'settled'
  | 'expired'
  | 'cancelled';

/**
 * Mirrors widgetSessionResp in internal/checkout/api/widget.go.
 *
 * Field names are the server's, deliberately. These types are erased at
 * runtime -- `getSession()` casts whatever JSON arrives -- so a name that
 * merely looks reasonable produces `undefined` at render time rather than
 * a compile error. Every field below is copied from the Go struct tags.
 */
export interface CheckoutSession {
  id: string;
  status: CheckoutStatus;
  client_uid: string;
  /** Absent until a payment method is chosen. */
  chain_id?: string;
  asset_id?: string;
  address_id?: string;
  /** The address itself, resolved server-side from address_id. */
  address?: string;
  /** Destination tag / memo. Only some chains use one. */
  memo?: string;
  /** Integer in BASE UNITS, as a string. Never parse with Number(). */
  expected_amount?: string;
  amount_tolerance?: string;
  /** For rendering expected_amount as a human amount. */
  asset_symbol?: string;
  asset_decimals: number;
  matched_transaction_id?: string;
  metadata?: unknown;
  return_url?: string;
  created_at: string;
  expires_at: string;
  updated_at: string;
}

/** Mirrors methodDTO in widget.go. */
export interface Method {
  chain_id: string;
  asset_id: string;
  /** Empty when the operator hardcoded the pair at session creation. */
  symbol: string;
  decimals: number;
  family: string;
  is_token: boolean;
}

/** The methods endpoint envelopes under `items`, not `methods`. */
export interface MethodsResponse {
  items: Method[];
}

export interface SelectRequest {
  chain_id: string;
  asset_id: string;
}

/** Mirrors eventDTO. There is no `type` field — switch on `status`. */
export interface CheckoutEvent {
  status: CheckoutStatus;
  at: string;
  /** NeoWallet's transaction id. NOT a chain tx hash. */
  matched_transaction_id?: string;
}

export interface EventEnvelope {
  session_status: CheckoutStatus;
  events: CheckoutEvent[];
  /** Server clock, to be sent back as the next `since`. There is no `cursor`. */
  now: string;
}

export class GoneError extends Error {
  code = 'GONE' as const;
  constructor(message: string) {
    super(message);
    this.name = 'GoneError';
  }
}

function readToken(): string {
  const t = new URLSearchParams(window.location.search).get('t');
  if (!t) throw new Error('missing token');
  return t;
}

const token = readToken();

export function getStoredToken(): string {
  return token;
}

async function request<T>(
  method: 'GET' | 'POST',
  path: string,
  body?: unknown,
): Promise<T> {
  const headers: Record<string, string> = {
    Authorization: `Checkout ${token}`,
    Accept: 'application/json',
  };
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  const res = await fetch(path, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    credentials: 'omit',
  });

  if (res.status === 204) return undefined as T;

  let payload: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      // non-JSON body — leave payload null
    }
  }

  if (!res.ok) {
    const msg =
      (payload && typeof payload === 'object' && 'error' in payload
        ? String((payload as { error: unknown }).error)
        : '') || `HTTP ${res.status}`;
    if (res.status === 410) throw new GoneError(msg);
    throw new Error(msg);
  }

  return payload as T;
}

export function getSession(): Promise<CheckoutSession> {
  return request<CheckoutSession>('GET', '/public/checkout/session');
}

export function getMethods(): Promise<MethodsResponse> {
  return request<MethodsResponse>('GET', '/public/checkout/methods');
}

export function selectMethod(input: SelectRequest): Promise<CheckoutSession> {
  return request<CheckoutSession>('POST', '/public/checkout/select', input);
}

export function getEvents(since?: string): Promise<EventEnvelope> {
  const qs = since ? `?since=${encodeURIComponent(since)}` : '';
  return request<EventEnvelope>('GET', `/public/checkout/events${qs}`);
}

export function cancel(): Promise<void> {
  return request<void>('POST', '/public/checkout/cancel', {});
}
